import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  FileText, Plus, Search, Download, Eye, Edit2, Trash2, 
  FileCheck, RefreshCw, MoreHorizontal, BookOpen, FolderOpen,
  AlertCircle, CheckCircle, Package, Wand2, FileSpreadsheet,
  ClipboardList, Shield, BarChart3, Users, Calendar, Briefcase
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Category icons and config
const categoryConfig = {
  audit_checklist: { icon: ClipboardList, color: 'text-blue-600', bgColor: 'bg-blue-500/10' },
  risk_assessment: { icon: Shield, color: 'text-red-600', bgColor: 'bg-red-500/10' },
  incident_investigation: { icon: AlertCircle, color: 'text-orange-600', bgColor: 'bg-orange-500/10' },
  capa_closure: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-500/10' },
  spi_report: { icon: BarChart3, color: 'text-purple-600', bgColor: 'bg-purple-500/10' },
  sop: { icon: FileText, color: 'text-slate-600', bgColor: 'bg-slate-500/10' },
  management_review: { icon: Users, color: 'text-indigo-600', bgColor: 'bg-indigo-500/10' },
  compliance_evidence: { icon: Package, color: 'text-cyan-600', bgColor: 'bg-cyan-500/10' },
  sms_manual: { icon: BookOpen, color: 'text-amber-600', bgColor: 'bg-amber-500/10' },
  training_record: { icon: Calendar, color: 'text-teal-600', bgColor: 'bg-teal-500/10' },
  safety_bulletin: { icon: FileCheck, color: 'text-rose-600', bgColor: 'bg-rose-500/10' },
  meeting_minutes: { icon: Briefcase, color: 'text-violet-600', bgColor: 'bg-violet-500/10' },
};

// Framework labels
const frameworkLabels = {
  gaca: 'GACA GACAR Part 5',
  easa: 'EASA ORO.GEN.200',
  icao: 'ICAO Annex 19',
  faa: 'FAA SMS',
  universal: 'Universal'
};

export default function DocumentTemplatesPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFramework, setFilterFramework] = useState('all');
  const [activeTab, setActiveTab] = useState('templates');
  
  // Dialog states
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showQuickGenerate, setShowQuickGenerate] = useState(false);
  const [showEvidencePackage, setShowEvidencePackage] = useState(false);
  const [showSMSManual, setShowSMSManual] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Quick generate states
  const [quickGenerateType, setQuickGenerateType] = useState('');
  const [quickGenerateId, setQuickGenerateId] = useState('');
  const [quickGenerateFramework, setQuickGenerateFramework] = useState('gaca');
  
  // SMS Manual options
  const [smsManualOptions, setSmsManualOptions] = useState({
    framework: 'gaca',
    revision: '1.0',
    include_appendices: true,
  });
  const [smsManualPreview, setSmsManualPreview] = useState(null);
  
  // Evidence package options
  const [evidenceOptions, setEvidenceOptions] = useState({
    framework: 'gaca',
    include_compliance_matrix: true,
    include_risk_register: true,
    include_spi_dashboard: true,
    include_training_records: true,
    include_audit_reports: true,
    include_capa_summary: true,
    include_incident_summary: true,
    include_policies: true,
  });
  
  // Data for linking
  const [incidents, setIncidents] = useState([]);
  const [risks, setRisks] = useState([]);
  const [audits, setAudits] = useState([]);
  const [capas, setCapas] = useState([]);

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
    fetchStats();
    fetchGeneratedDocs();
    fetchLinkingData();
  }, [api]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/document-templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/document-templates/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/document-templates/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchGeneratedDocs = async () => {
    try {
      const response = await api.get('/document-templates/generated?limit=20');
      setGeneratedDocs(response.data);
    } catch (error) {
      console.error('Failed to fetch generated docs:', error);
    }
  };

  const fetchLinkingData = async () => {
    try {
      const [incRes, riskRes, auditRes, capaRes] = await Promise.all([
        api.get('/incidents?limit=50'),
        api.get('/risks?limit=50'),
        api.get('/audits?limit=50'),
        api.get('/capas?limit=50'),
      ]);
      setIncidents(incRes.data || []);
      setRisks(riskRes.data || []);
      setAudits(auditRes.data || []);
      setCapas(capaRes.data || []);
    } catch (error) {
      console.error('Failed to fetch linking data:', error);
    }
  };

  const handleQuickGenerate = async () => {
    if (!quickGenerateType) {
      toast.error('Please select a document type');
      return;
    }
    
    setIsGenerating(true);
    
    try {
      let endpoint = '';
      let needsId = true;
      
      switch (quickGenerateType) {
        case 'audit':
          endpoint = `/document-templates/generate/audit-checklist/${quickGenerateId}?framework=${quickGenerateFramework}`;
          break;
        case 'risk':
          endpoint = `/document-templates/generate/risk-assessment/${quickGenerateId}`;
          break;
        case 'incident':
          endpoint = `/document-templates/generate/incident-investigation/${quickGenerateId}`;
          break;
        case 'capa':
          endpoint = `/document-templates/generate/capa-closure/${quickGenerateId}`;
          break;
        case 'spi':
          endpoint = `/document-templates/generate/spi-dashboard`;
          needsId = false;
          break;
        case 'compliance':
          endpoint = `/document-templates/generate/compliance-matrix/${quickGenerateFramework}`;
          needsId = false;
          break;
        default:
          toast.error('Unknown document type');
          return;
      }
      
      if (needsId && !quickGenerateId) {
        toast.error('Please select a record');
        setIsGenerating(false);
        return;
      }
      
      const response = await api.post(endpoint, {}, { responseType: 'blob' });
      
      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quickGenerateType}_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Document generated successfully!');
      setShowQuickGenerate(false);
      fetchGeneratedDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate document');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateEvidencePackage = async () => {
    setIsGenerating(true);
    
    try {
      const params = new URLSearchParams();
      params.append('framework', evidenceOptions.framework);
      Object.keys(evidenceOptions).forEach(key => {
        if (key !== 'framework') {
          params.append(key, evidenceOptions[key]);
        }
      });
      
      const response = await api.post(`/document-templates/generate/evidence-package?${params.toString()}`, {}, { responseType: 'blob' });
      
      // Download the ZIP file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Evidence_Package_${evidenceOptions.framework.toUpperCase()}_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Evidence package generated successfully!');
      setShowEvidencePackage(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate evidence package');
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchSMSManualPreview = async (framework) => {
    try {
      const response = await api.get(`/document-templates/sms-manual/preview/${framework}`);
      setSmsManualPreview(response.data);
    } catch (error) {
      console.error('Failed to fetch SMS Manual preview:', error);
    }
  };

  const handleGenerateSMSManual = async () => {
    setIsGenerating(true);
    
    try {
      const params = new URLSearchParams();
      params.append('revision', smsManualOptions.revision);
      params.append('include_appendices', smsManualOptions.include_appendices);
      
      const response = await api.post(
        `/document-templates/sms-manual/generate/${smsManualOptions.framework}?${params.toString()}`,
        {},
        { responseType: 'blob' }
      );
      
      // Download the PDF file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SMS_Manual_${smsManualOptions.framework.toUpperCase()}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('SMS Manual generated successfully!');
      setShowSMSManual(false);
      fetchGeneratedDocs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate SMS Manual');
    } finally {
      setIsGenerating(false);
    }
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat?.label || value;
  };

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchQuery || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    const matchesFramework = filterFramework === 'all' || template.regulatory_framework === filterFramework;
    
    return matchesSearch && matchesCategory && matchesFramework;
  });

  return (
    <div className="space-y-6 p-6" data-testid="document-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Wand2 className="h-6 w-6 text-indigo-600" />
            Document Intelligence Engine
          </h1>
          <p className="text-muted-foreground mt-1">
            Generate regulated documents, audit checklists, and compliance evidence packages
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => {
            setShowSMSManual(true);
            fetchSMSManualPreview(smsManualOptions.framework);
          }} className="bg-amber-500/10 border-amber-500/30 text-amber-700 hover:bg-amber-500/20" data-testid="sms-manual-btn">
            <BookOpen className="h-4 w-4 mr-2" />
            SMS Manual Builder
          </Button>
          <Button variant="outline" onClick={() => setShowEvidencePackage(true)} data-testid="evidence-package-btn">
            <Package className="h-4 w-4 mr-2" />
            Evidence Package
          </Button>
          <Button onClick={() => setShowQuickGenerate(true)} data-testid="quick-generate-btn">
            <Wand2 className="h-4 w-4 mr-2" />
            Quick Generate
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border-indigo-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Templates</p>
                  <p className="text-2xl font-bold text-indigo-600">{stats.total_templates}</p>
                </div>
                <FileText className="h-8 w-8 text-indigo-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Generated (Total)</p>
                  <p className="text-2xl font-bold text-green-600">{stats.total_generated}</p>
                </div>
                <FileCheck className="h-8 w-8 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">This Month</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.generated_this_month}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Categories</p>
                  <p className="text-2xl font-bold text-purple-600">{Object.keys(stats.by_category || {}).length}</p>
                </div>
                <FolderOpen className="h-8 w-8 text-purple-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="h-4 w-4 mr-2" />
            Template Library
          </TabsTrigger>
          <TabsTrigger value="generated" data-testid="tab-generated">
            <FileCheck className="h-4 w-4 mr-2" />
            Generated Documents
            {generatedDocs.length > 0 && (
              <Badge variant="secondary" className="ml-2">{generatedDocs.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Template Library Tab */}
        <TabsContent value="templates" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
                
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-48" data-testid="filter-category">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filterFramework} onValueChange={setFilterFramework}>
                  <SelectTrigger className="w-44" data-testid="filter-framework">
                    <SelectValue placeholder="Framework" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Frameworks</SelectItem>
                    <SelectItem value="gaca">GACA</SelectItem>
                    <SelectItem value="easa">EASA</SelectItem>
                    <SelectItem value="icao">ICAO</SelectItem>
                    <SelectItem value="universal">Universal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Template Grid */}
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">Loading templates...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <p className="mt-2 text-muted-foreground">No templates found</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map(template => {
                const config = categoryConfig[template.category] || categoryConfig.sop;
                const Icon = config.icon;
                
                return (
                  <Card 
                    key={template.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer group"
                    data-testid={`template-card-${template.id}`}
                    onClick={() => {
                      setSelectedTemplate(template);
                      setShowGenerateDialog(true);
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${config.bgColor}`}>
                          <Icon className={`h-5 w-5 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-sm truncate group-hover:text-indigo-600 transition-colors">{template.name}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {getCategoryLabel(template.category)}
                          </p>
                        </div>
                        <Eye className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      
                      {template.description && (
                        <p className="text-xs text-muted-foreground mt-3 line-clamp-2">
                          {template.description}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs">
                            {frameworkLabels[template.regulatory_framework] || template.regulatory_framework}
                          </Badge>
                          {template.is_system_template && (
                            <Badge className="text-xs bg-indigo-500/10 text-indigo-600">System</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          Used {template.usage_count}x
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Generated Documents Tab */}
        <TabsContent value="generated" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              {generatedDocs.length === 0 ? (
                <div className="p-8 text-center">
                  <FileCheck className="h-12 w-12 mx-auto text-muted-foreground/50" />
                  <p className="mt-2 text-muted-foreground">No documents generated yet</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowQuickGenerate(true)}>
                    <Wand2 className="h-4 w-4 mr-2" />
                    Generate Your First Document
                  </Button>
                </div>
              ) : (
                <div className="divide-y">
                  {generatedDocs.map(doc => {
                    const config = categoryConfig[doc.template_category] || categoryConfig.sop;
                    const Icon = config.icon;
                    
                    return (
                      <div key={doc.id} className="p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${config.bgColor}`}>
                              <Icon className={`h-4 w-4 ${config.color}`} />
                            </div>
                            <div>
                              <h4 className="font-medium text-sm">{doc.document_title}</h4>
                              <p className="text-xs text-muted-foreground">
                                {doc.template_name} • Generated {new Date(doc.generated_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {doc.output_format.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {(doc.file_size / 1024).toFixed(1)} KB
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => window.open(`${import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}${doc.file_url}`, '_blank')}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Generate Dialog */}
      <Dialog open={showQuickGenerate} onOpenChange={setShowQuickGenerate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-indigo-600" />
              Quick Generate Document
            </DialogTitle>
            <DialogDescription>
              Auto-generate regulated documents from your safety data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={quickGenerateType} onValueChange={setQuickGenerateType}>
                <SelectTrigger data-testid="quick-gen-type">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="audit">Audit Checklist</SelectItem>
                  <SelectItem value="risk">Risk Assessment Report</SelectItem>
                  <SelectItem value="incident">Incident Investigation Report</SelectItem>
                  <SelectItem value="capa">CAPA Closure Report</SelectItem>
                  <SelectItem value="spi">SPI Dashboard Report</SelectItem>
                  <SelectItem value="compliance">Compliance Matrix</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Conditional fields based on type */}
            {quickGenerateType === 'audit' && (
              <>
                <div className="space-y-2">
                  <Label>Select Audit</Label>
                  <Select value={quickGenerateId} onValueChange={setQuickGenerateId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select audit" />
                    </SelectTrigger>
                    <SelectContent>
                      {audits.map(audit => (
                        <SelectItem key={audit.id} value={audit.id}>
                          {audit.audit_number || audit.title || audit.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Regulatory Framework</Label>
                  <Select value={quickGenerateFramework} onValueChange={setQuickGenerateFramework}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gaca">GACA GACAR Part 5</SelectItem>
                      <SelectItem value="easa">EASA ORO.GEN.200</SelectItem>
                      <SelectItem value="icao">ICAO Annex 19</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {quickGenerateType === 'risk' && (
              <div className="space-y-2">
                <Label>Select Risk</Label>
                <Select value={quickGenerateId} onValueChange={setQuickGenerateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select risk" />
                  </SelectTrigger>
                  <SelectContent>
                    {risks.map(risk => (
                      <SelectItem key={risk.id} value={risk.id}>
                        {risk.risk_number || risk.title || risk.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {quickGenerateType === 'incident' && (
              <div className="space-y-2">
                <Label>Select Incident</Label>
                <Select value={quickGenerateId} onValueChange={setQuickGenerateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select incident" />
                  </SelectTrigger>
                  <SelectContent>
                    {incidents.map(inc => (
                      <SelectItem key={inc.id} value={inc.id}>
                        {inc.incident_number || inc.title || inc.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {quickGenerateType === 'capa' && (
              <div className="space-y-2">
                <Label>Select CAPA</Label>
                <Select value={quickGenerateId} onValueChange={setQuickGenerateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select CAPA" />
                  </SelectTrigger>
                  <SelectContent>
                    {capas.map(capa => (
                      <SelectItem key={capa.id} value={capa.id}>
                        {capa.capa_number || capa.title || capa.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {quickGenerateType === 'compliance' && (
              <div className="space-y-2">
                <Label>Regulatory Framework</Label>
                <Select value={quickGenerateFramework} onValueChange={setQuickGenerateFramework}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gaca">GACA GACAR Part 5</SelectItem>
                    <SelectItem value="easa">EASA ORO.GEN.200</SelectItem>
                    <SelectItem value="icao">ICAO Annex 19</SelectItem>
                    <SelectItem value="universal">Multi-Regulatory</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {(quickGenerateType === 'spi') && (
              <p className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-lg">
                This report will include all active Safety Performance Indicators with current values and trends.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickGenerate(false)}>
              Cancel
            </Button>
            <Button onClick={handleQuickGenerate} disabled={isGenerating} data-testid="generate-btn">
              {isGenerating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Generate PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evidence Package Dialog */}
      <Dialog open={showEvidencePackage} onOpenChange={setShowEvidencePackage}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-cyan-600" />
              Generate Evidence Package
            </DialogTitle>
            <DialogDescription>
              Create a complete regulatory submission package with all compliance evidence
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Regulatory Framework</Label>
              <Select 
                value={evidenceOptions.framework} 
                onValueChange={(v) => setEvidenceOptions({...evidenceOptions, framework: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gaca">GACA GACAR Part 5</SelectItem>
                  <SelectItem value="easa">EASA ORO.GEN.200</SelectItem>
                  <SelectItem value="icao">ICAO Annex 19</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-3">
              <Label>Include in Package:</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'include_compliance_matrix', label: 'Compliance Matrix' },
                  { key: 'include_risk_register', label: 'Risk Register' },
                  { key: 'include_spi_dashboard', label: 'SPI Dashboard' },
                  { key: 'include_training_records', label: 'Training Records' },
                  { key: 'include_audit_reports', label: 'Audit Reports' },
                  { key: 'include_capa_summary', label: 'CAPA Summary' },
                  { key: 'include_incident_summary', label: 'Incident Summary' },
                  { key: 'include_policies', label: 'Approved Policies' },
                ].map(item => (
                  <div key={item.key} className="flex items-center gap-2">
                    <Checkbox
                      id={item.key}
                      checked={evidenceOptions[item.key]}
                      onCheckedChange={(checked) => setEvidenceOptions({...evidenceOptions, [item.key]: checked})}
                    />
                    <label htmlFor={item.key} className="text-sm cursor-pointer">{item.label}</label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>Output:</strong> ZIP file containing PDF reports, JSON data exports, and an index file for regulatory submission.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEvidencePackage(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateEvidencePackage} disabled={isGenerating} data-testid="generate-package-btn">
              {isGenerating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  Generate Package
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template View/Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-indigo-600" />
              Template Details
            </DialogTitle>
            <DialogDescription>
              View template information and generate a document
            </DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4">
              {/* Template Info */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${categoryConfig[selectedTemplate.category]?.bgColor || 'bg-slate-500/10'}`}>
                    {(() => {
                      const TemplateIcon = categoryConfig[selectedTemplate.category]?.icon || FileText;
                      return <TemplateIcon className={`h-5 w-5 ${categoryConfig[selectedTemplate.category]?.color || 'text-slate-600'}`} />;
                    })()}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{selectedTemplate.name}</h3>
                    <p className="text-sm text-muted-foreground">{getCategoryLabel(selectedTemplate.category)}</p>
                  </div>
                </div>
                
                {selectedTemplate.description && (
                  <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
                )}
                
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">
                    {frameworkLabels[selectedTemplate.regulatory_framework] || selectedTemplate.regulatory_framework}
                  </Badge>
                  {selectedTemplate.is_system_template && (
                    <Badge className="bg-indigo-500/10 text-indigo-600">System Template</Badge>
                  )}
                  <Badge variant="secondary">Used {selectedTemplate.usage_count}x</Badge>
                </div>
              </div>
              
              {/* Output Format */}
              <div className="space-y-2">
                <Label>Output Format</Label>
                <Select defaultValue="pdf">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF Document</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Template Fields Preview */}
              {selectedTemplate.fields && selectedTemplate.fields.length > 0 && (
                <div className="space-y-2">
                  <Label>Template Fields</Label>
                  <div className="p-3 bg-muted/30 rounded-lg text-sm space-y-1">
                    {selectedTemplate.fields.map((field, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-muted-foreground">•</span>
                        <span>{field.label || field.name}</span>
                        {field.required && <Badge variant="outline" className="text-xs">Required</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                setShowGenerateDialog(false);
                setShowQuickGenerate(true);
                // Pre-select the template type if possible
                if (selectedTemplate) {
                  const typeMap = {
                    'audit_checklist': 'audit',
                    'risk_assessment': 'risk',
                    'incident_investigation': 'incident',
                    'capa_closure': 'capa',
                    'spi_report': 'spi',
                    'compliance_evidence': 'compliance'
                  };
                  setQuickGenerateType(typeMap[selectedTemplate.category] || '');
                }
              }}
              data-testid="generate-from-template-btn"
            >
              <Wand2 className="h-4 w-4 mr-2" />
              Generate Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SMS Manual Builder Dialog */}
      <Dialog open={showSMSManual} onOpenChange={setShowSMSManual}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-amber-600" />
              SMS Manual Auto-Builder
            </DialogTitle>
            <DialogDescription>
              Generate a complete Safety Management System Manual mapped to regulatory frameworks
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Framework Selection */}
            <div className="grid grid-cols-2 gap-4">
              <Card 
                className={`cursor-pointer transition-all ${smsManualOptions.framework === 'gaca' ? 'border-amber-500 bg-amber-500/5' : 'hover:border-muted-foreground/50'}`}
                onClick={() => {
                  setSmsManualOptions({...smsManualOptions, framework: 'gaca'});
                  fetchSMSManualPreview('gaca');
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <BookOpen className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">GACAR Part 5</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Saudi GACA Safety Management System
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">5 Chapters</Badge>
                        <Badge variant="outline" className="text-xs">3 Appendices</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer transition-all ${smsManualOptions.framework === 'easa' ? 'border-blue-500 bg-blue-500/5' : 'hover:border-muted-foreground/50'}`}
                onClick={() => {
                  setSmsManualOptions({...smsManualOptions, framework: 'easa'});
                  fetchSMSManualPreview('easa');
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">EASA ORO.GEN.200</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        European Management System
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">7 Chapters</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Revision Number</Label>
                <Input
                  value={smsManualOptions.revision}
                  onChange={(e) => setSmsManualOptions({...smsManualOptions, revision: e.target.value})}
                  placeholder="1.0"
                />
              </div>
              <div className="space-y-2">
                <Label className="invisible">Options</Label>
                <div className="flex items-center gap-2 h-10">
                  <Checkbox
                    id="include_appendices"
                    checked={smsManualOptions.include_appendices}
                    onCheckedChange={(checked) => setSmsManualOptions({...smsManualOptions, include_appendices: checked})}
                  />
                  <label htmlFor="include_appendices" className="text-sm cursor-pointer">
                    Include Appendices
                  </label>
                </div>
              </div>
            </div>
            
            {/* Preview */}
            {smsManualPreview && (
              <div className="border rounded-lg p-4 bg-muted/30">
                <h4 className="font-medium flex items-center gap-2 mb-3">
                  <Eye className="h-4 w-4" />
                  Manual Structure Preview
                </h4>
                <div className="text-sm space-y-2 max-h-60 overflow-y-auto">
                  {smsManualPreview.chapters?.map((chapter, idx) => (
                    <div key={idx} className="border-l-2 border-amber-500/50 pl-3">
                      <div className="font-medium text-amber-700">
                        {chapter.is_appendix ? `Appendix ${chapter.chapter_number}` : `Chapter ${chapter.chapter_number}`}: {chapter.title}
                      </div>
                      {chapter.title_ar && (
                        <div className="text-xs text-muted-foreground text-right">{chapter.title_ar}</div>
                      )}
                      {chapter.regulatory_reference && (
                        <div className="text-xs text-blue-600">{chapter.regulatory_reference}</div>
                      )}
                      <div className="pl-4 mt-1 space-y-0.5">
                        {chapter.sections?.map((section, sIdx) => (
                          <div key={sIdx} className="text-xs text-muted-foreground flex items-center gap-2">
                            <span>{section.section_number} {section.title}</span>
                            {section.has_auto_populate && (
                              <Badge variant="outline" className="text-[10px] py-0 px-1">Auto-fill</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Info Box */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Auto-populated content includes:</strong>
                <br/>• Organization name and key personnel
                <br/>• Risk register statistics
                <br/>• Safety policy sections
                <br/>• Current date and revision
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSMSManual(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateSMSManual} 
              disabled={isGenerating}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="generate-sms-manual-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating Manual...
                </>
              ) : (
                <>
                  <BookOpen className="h-4 w-4 mr-2" />
                  Generate SMS Manual
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
